const EMAIL_STORAGE_KEY = 'nestoria_user_email';
const GOOGLE_ONE_TAP_EMAIL_KEY = 'email';
const ALERT_URL = '/wl/api/notifications/email/subscription';

export interface AlertFilters {
    bedrooms: string;
    bathrooms: string;
    priceMin: string;
    priceMax: string;
    sizeMin: string;
    sizeMax: string;
}

export const defaultAlertFilters: AlertFilters = {
    bedrooms: '',
    bathrooms: '',
    priceMin: '',
    priceMax: '',

    sizeMin: '',
    sizeMax: '',
};

export interface AlertParams {
    location: string;
    pageViewId: string;
    origin: string;
    receiveDems: boolean;
    listingType: string;
    propertyType: string;
    filters: AlertFilters;
}

export class EmailCaptureApiCaller {
    isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    async submitAlert(email: string, alertParams: AlertParams): Promise<void> {
        await this.submitSingleAlert(email, alertParams.location, alertParams);
    }

    async submitAlerts(email: string, locations: string[], alertParams: AlertParams): Promise<void> {
        const allLocations = [alertParams.location, ...locations];
        for (const location of allLocations) {
            await this.submitSingleAlert(email, location, alertParams);
        }
    }

    private async submitSingleAlert(email: string, locAlert: string, alertParams: AlertParams): Promise<void> {
        const urlParams = new URLSearchParams(window.location.search);

        const params = new URLSearchParams({
            location: alertParams.location,
            locAlert: locAlert,
            email: email,
            t_pvid: alertParams.pageViewId,
            t_alert_sec: '1',
            tracking: '',
            origin: alertParams.origin,
            utm_source: urlParams.get('utm_source') || '',
            utm_medium: urlParams.get('utm_medium') || '',
            receiveDems: alertParams.receiveDems ? 'on' : 'off',
            listing_type: alertParams.listingType,
            property_type: alertParams.propertyType,
        });

        const filters = alertParams.filters;
        params.set('bedrooms', filters.bedrooms);
        params.set('bathrooms', filters.bathrooms);
        params.set('price_min', filters.priceMin);
        params.set('price_max', filters.priceMax);
        params.set('size_min', filters.sizeMin);
        params.set('size_max', filters.sizeMax);

        const response = await fetch(ALERT_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: params.toString()
        });

        if (!response.ok) {
            throw new Error('Failed to submit alert');
        }
    }

    saveEmailToStorage(email: string): void {
        try {
            localStorage.setItem(EMAIL_STORAGE_KEY, email);
        } catch (e) {
            // localStorage not available
        }
    }

    getEmailFromStorage(): string | null {
        try {
            const nestoriaEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
            if (nestoriaEmail) {
                return nestoriaEmail;
            }
            const googleOneTapEmail = localStorage.getItem(GOOGLE_ONE_TAP_EMAIL_KEY);
            if (googleOneTapEmail) {
                return googleOneTapEmail;
            }
            return null;
        } catch (e) {
            return null;
        }
    }

    toggleError(errorElement: HTMLElement | null, emailInput: HTMLInputElement | null, show: boolean): void {
        if (show) {
            errorElement?.classList.add('visible');
            emailInput?.classList.add('error');
        } else {
            errorElement?.classList.remove('visible');
            emailInput?.classList.remove('error');
        }
    }

    getAlertParamsFromContainer(container: HTMLElement | null, form: HTMLFormElement | null): AlertParams {
        const newsletterCheckbox = form?.querySelector('input[name="receiveDems"]') as HTMLInputElement | null;
        const dataset = container?.dataset || {};

        return {
            location: dataset.location || '',
            pageViewId: dataset.pageViewId || '',
            origin: dataset.origin || '1',
            receiveDems: newsletterCheckbox?.checked || false,
            listingType: dataset.listingType || '',
            propertyType: dataset.propertyType || '',
            filters: {
                bedrooms: dataset.bedrooms || '',
                bathrooms: dataset.bathrooms || '',
                priceMin: dataset.priceMin || '',
                priceMax: dataset.priceMax || '',
                sizeMin: dataset.sizeMin || '',
                sizeMax: dataset.sizeMax || '',
            }
        };
    }
}
